import {
  Component,
  Input,
  Output,
  OnChanges,
  EventEmitter,
  ChangeDetectionStrategy,
  SimpleChanges,
  OnInit,
  ChangeDetectorRef,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { format } from 'date-fns';
import { merge } from 'lodash';
import { EditorInitEvent, Editor } from 'primeng/editor';
import Quill from 'quill';
import { Subject, takeUntil } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { DatesService, DialogService, DynamicDateRange, TrackingService } from '@hub-components';
import { WysiwygService } from './wysiwyg.service';
import { WidgetStyle, WidgetStyleOptions } from '../../interfaces/widget-style.interface';

@Component({
  selector: 'wysiwyg-widget',
  templateUrl: './wysiwyg.component.html',
  styleUrls: ['./wysiwyg.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})

export class WysiwygComponent implements OnInit, OnChanges, OnDestroy {
  static isBasicWidget = true;
  static supportedOptions = [
    {
      bindTo: 'wysiwygOptions',
      type: 'wysiwygOptions',
      options: [
        {
          bindTo: 'type',
        },
        {
          bindTo: 'globalDateRange',
        },
      ],
    },
    {
      bindTo: 'html',
    },
    {
      bindTo: 'toolbar',
      type: 'toolbarInputSwitch',
    },
    {
      bindTo: 'placement',
      type: 'textFormat',
      options: [
        {
          bindTo: 'position',
        },
      ],
    },
  ];

  static supportedStyle: WidgetStyleOptions[] = [
    {
      bindTo: 'backgroundColor',
      label: 'Background Color',
    },
    {
      bindTo: 'borderWidth',
      label: 'Thickness',
    },
    {
      bindTo: 'shadow',
      label: 'Size',
    },
    {
      bindTo: 'shadowColor',
      label: 'Color',
    },
    {
      bindTo: 'shadowOpacity',
      label: 'Opacity',
    },
    {
      bindTo: 'borderColor',
      label: 'Color',
    },
    {
      bindTo: 'borderRadius',
      label: 'Corner Radius',
    },
  ];

  @Input() style: WidgetStyle;
  @Input() chartOptions: any;
  @Input() dashboardInterval: DynamicDateRange;
  @Input() active: boolean = false;
  @Output() contentChanged: EventEmitter<any> = new EventEmitter();

  @ViewChild('editor') editor: Editor;

  public backgroundColor: string;
  public verticalAlignment: string = 'top';
  public textEditorFormControl: FormControl = new FormControl();
  public fontSizes: string[];
  public textColors: string[];
  public readonly defaultFontSize: string = '14px';

  private destroyed$ = new Subject<void>();
  private resizeObserver?: ResizeObserver;
  private formUrlRegex = /^(https?:\/\/|\/\/)?([-a-zA-Z0-9@:%._+~#=]+\.)+[a-zA-Z0-9()]+?(\/[-a-zA-Z0-9()@:%_+.~#?&//=]*)?$/i;
  public linkUrlControl: FormControl = new FormControl('', [Validators.pattern(this.formUrlRegex)]);

  constructor(
    private wysiwygService: WysiwygService,
    private datesService: DatesService,
    private changeDetectorRef: ChangeDetectorRef,
    private dialogService: DialogService,
    private trackingService: TrackingService,
  ) { }

  public ngOnInit(): void {
    this.fontSizes = this.wysiwygService.fontSizes;
    this.textColors = this.wysiwygService.textColors;
    this.subscribeToTextEditorChanges();
    this.subscribeToLinkUrlChanges();
    this.initQuillCustomFontSizes();
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.resizeObserver?.disconnect();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (this.style) {
      this.backgroundColor = this.style.backgroundColor;
    }

    if (this.chartOptions) {
      const currentType = this.chartOptions.wysiwygOptions?.type;
      const previousType = changes?.options?.previousValue?.wysiwygOptions?.type;
      const typeChanged = previousType && previousType !== currentType;
      const htmlChanged = !!changes?.options
        && changes.options.currentValue?.html !== changes.options.previousValue?.html;
      const intervalChanged = !!changes?.dashboardInterval;

      if (typeChanged || (currentType === 'configs' && (intervalChanged || htmlChanged))) {
        this.textEditorFormControl.patchValue(currentType === 'configs' ? this.getDates() : '', { emitEvent: false });
      } else if (htmlChanged && this.textEditorFormControl.value !== this.chartOptions.html) {
        this.textEditorFormControl.patchValue(this.chartOptions.html, { emitEvent: false });
      }

      this.verticalAlignment = this.chartOptions.placement?.position ?? 'top';
    }

    if (changes['active']) {
      if (this.active) {
        this.setupResizeObserver();
      } else {
        this.cleanupResizeObserver();
      }
    }
  }

  public onEditorInit(editorInitEvent: EditorInitEvent): void {
    const editor = editorInitEvent.editor;
    const tooltipStyle = editor.theme?.tooltip?.root?.style;

    if (this.active) {
      editor.focus();
      const length = editor.getLength();
      editor.setSelection(length, length);
      this.positionToolbar();
    }

    if (tooltipStyle) {
      tooltipStyle.display = 'none';
    }

    editor.root.addEventListener('click', (e: MouseEvent) => {
      const anchor = (e.target as HTMLElement).closest('a');
      if (anchor && !this.active) {
        e.preventDefault();
        window.open(anchor.href, '_blank');
      }
    });

    const toolbar = editor.getModule('toolbar');
    toolbar?.addHandler('link', () => {
      const range = editor.getSelection();
      const format = editor.getFormat(range);
      if (range.length) {
        this.dialogService.openDialog('linkDialog');
        if (format.link) {
          this.linkUrlControl.setValue(format.link);
        } else {
          this.linkUrlControl.setValue('');
        }
      }

      this.changeDetectorRef.markForCheck();
    });
  }

  public applyLink(): void {
    const rawUrl = this.linkUrlControl.value;
    if (!rawUrl) {
      return;
    }

    const finalUrl = this.prepareUrl(rawUrl);
    const editor = this.editor.getQuill();
    editor.format('link', finalUrl);
    this.textEditorFormControl.setValue(editor.root.innerHTML, { emitEvent: true });
    this.dialogService.closeDialog('linkDialog');
    this.trackingService.trackEvent('Text Widget: Link Added');
  }

  public removeLink(): void {
    const editor = this.editor.getQuill();
    editor.format('link', null);
    this.linkUrlControl.setValue('');
    this.dialogService.closeDialog('linkDialog');
    this.trackingService.trackEvent('Text Widget: Link Removed');
    this.changeDetectorRef.detectChanges();
  }

  private prepareUrl(url: string): string {
    if (url.includes('//')) {
      return url;
    }

    return '//' + url;
  }

  private subscribeToTextEditorChanges() {
    this.textEditorFormControl.valueChanges.pipe(
      debounceTime(400),
      takeUntil(this.destroyed$),
    ).subscribe(value => {
      this.chartOptions = merge({}, this.chartOptions, { html: value });
      this.contentChanged.emit(this.chartOptions);
    });
  }

  private subscribeToLinkUrlChanges() {
    this.linkUrlControl.valueChanges.pipe(
      takeUntil(this.destroyed$),
    ).subscribe(() => {
      this.changeDetectorRef.detectChanges();
    });
  }

  private initQuillCustomFontSizes() {
    const fontStyleSize = Quill.import('attributors/style/size') as any;
    fontStyleSize.whitelist = this.fontSizes;
    Quill.register(fontStyleSize, true);
  }

  private getDates(): string {
    const date = this.datesService.parseValue(this.dashboardInterval);
    const startDate = format(date.startDate, 'yyyy-MM-dd');
    const endDate = format(date.endDate, 'yyyy-MM-dd');
    const dateIntervalRegex = /(\d{4}-\d{2}-\d{2})\s*-\s*(\d{4}-\d{2}-\d{2})/g;
    const dateRegex = /\d{4}-\d{2}-\d{2}/g;
    const text = this.textEditorFormControl.value;

    if (dateRegex.test(text)) {
      return text.replace(dateIntervalRegex, `${startDate} - ${endDate}`);
    } else if (dateRegex.test(this.chartOptions.html)) {
      return this.chartOptions.html;
    } else {
      return `${startDate} - ${endDate}`;
    }
  }

  private setupResizeObserver(): void {
    if (!this.editor || this.resizeObserver) return;

    const editorElement = this.editor.el.nativeElement;
    const widgetContainer = editorElement.closest('.widget-container') as HTMLElement;

    if (!widgetContainer) return;

    this.resizeObserver = new ResizeObserver(() => {
      this.positionToolbar();
    });

    this.resizeObserver.observe(widgetContainer);
  }

  private cleanupResizeObserver(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
  }

  private positionToolbar(): void {
    if (!this.editor) return;

    const editorElement = this.editor.el.nativeElement;
    const toolbarElement = editorElement.querySelector('.ql-toolbar') as HTMLElement;
    const widgetContainer = editorElement.closest('.widget-container') as HTMLElement;

    if (!toolbarElement || !widgetContainer) return;

    const canvasContainer = widgetContainer.closest('.wrapper') || document.body;
    const canvasRect = canvasContainer.getBoundingClientRect();
    const containerRect = widgetContainer.getBoundingClientRect();
    const toolbarHeight = toolbarElement.getBoundingClientRect().height;
    const spaceAbove = containerRect.top - canvasRect.top;
    const containerComputedStyle = window.getComputedStyle(widgetContainer);

    if (containerComputedStyle.position === 'static') {
      widgetContainer.style.position = 'relative';
    }

    Object.assign(toolbarElement.style, {
      position: 'absolute',
      left: '0px',
      right: '0px',
      zIndex: '1000',
      top: spaceAbove >= toolbarHeight + 10 ? `-${toolbarHeight}px` : '100%',
    });
  }
}
